import React from 'react';
import { Col, Row } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import uuid from 'react-uuid';

import {
  addCustomField,
  addCustomFieldsToParent,
  updateCustomFieldParent,
  updateCustomFields,
} from 'redux/actions/customFieldActions';
import FieldType from 'shared/helpers/fieldType';
import { CustomFieldProps } from 'shared/prop-types/ReducerProps';
import { emptyString } from 'shared/utils';

import GroupForm from './GroupForm';
import TextFieldForm from './TextFieldForm';
import TextAreaForm from './TextAreaForm';
import ListForm from './ListForm';
import GenericForm from './GenericForm';
import { FieldFormWrapper } from './styles';

const FieldForm = ({ isNew, fieldFormType, selectedField, onCancel }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { selectedDefaultLanguage } = useSelector((state) => state.topBar);
  const onAddGroupField = (values) => {
    if (isNew) {
      const id = uuid();
      const newGroupField = {
        id,
        i18List: [
          {
            i18Code: selectedDefaultLanguage,
            i18Name: values.groupTitle,
          },
        ],
        type: FieldType.GROUP,
        size: 0,
        default: false,
        members: [],
        isNew,
      };

      dispatch(addCustomField(newGroupField));
      dispatch(addCustomFieldsToParent({ parentId: id, fieldIds: values.groupFields }));
    } else {
      const updatedGroupField = {
        ...selectedField,
        i18List: [
          {
            i18Code: selectedDefaultLanguage,
            i18Name: values.groupTitle,
          },
        ],
        updated: true,
      };
      dispatch(
        updateCustomFieldParent({
          updatedCustomField: updatedGroupField,
          childFieldIds: values.groupFields,
        })
      );
    }
    onCancel();
  };

  const onAddTextField = (values) => {
    if (isNew) {
      const newTextField = {
        id: uuid(),
        parentId: values.groupId,
        i18List: [
          {
            i18Code: selectedDefaultLanguage,
            i18Name: values.fieldTitle,
          },
        ],
        type: FieldType.TEXT,
        size: !emptyString(values.maxCharAllowed) ? parseInt(values.maxCharAllowed, 10) : 100,
        default: false,
        members: [],
        isNew,
      };
      dispatch(addCustomField(newTextField));
    } else {
      const oldI18List = selectedField.i18List.filter((x) => selectedDefaultLanguage !== x.i18Code);
      const updatedField = {
        ...selectedField,
        parentId: values.groupId,
        i18List: [
          ...oldI18List,
          {
            i18Code: selectedDefaultLanguage,
            i18Name: values.fieldTitle,
          },
        ],
        size: !emptyString(values.maxCharAllowed) ? parseInt(values.maxCharAllowed, 10) : 100,
        updated: true,
      };
      dispatch(updateCustomFields(updatedField));
    }
    onCancel();
  };

  const onAddTextArea = (values) => {
    if (isNew) {
      const newTextArea = {
        id: uuid(),
        parentId: values.groupId,
        i18List: [
          {
            i18Code: selectedDefaultLanguage,
            i18Name: values.fieldTitle,
          },
        ],
        type: FieldType.TEXT_AREA,
        size: !emptyString(values.maxCharAllowed) ? parseInt(values.maxCharAllowed, 10) : 300,
        default: false,
        members: [],
        isNew,
      };
      dispatch(addCustomField(newTextArea));
    } else {
      const oldI18List = selectedField.i18List.filter((x) => selectedDefaultLanguage !== x.i18Code);
      const updatedField = {
        ...selectedField,
        parentId: values.groupId,
        i18List: [
          ...oldI18List,
          {
            i18Code: selectedDefaultLanguage,
            i18Name: values.fieldTitle,
          },
        ],
        size: !emptyString(values.maxCharAllowed) ? parseInt(values.maxCharAllowed, 10) : 300,
        updated: true,
      };
      dispatch(updateCustomFields(updatedField));
    }
    onCancel();
  };

  const onAddListFields = (multiple, values, members) => {
    if (isNew) {
      const newListField = {
        id: uuid(),
        parentId: values.groupId,
        i18List: [
          {
            i18Code: selectedDefaultLanguage,
            i18Name: values.fieldTitle,
          },
        ],
        type: multiple ? FieldType.MULTI_LIST : FieldType.LIST,
        size: 0,
        default: false,
        members,
        isNew,
      };
      dispatch(addCustomField(newListField));
    } else {
      const oldI18List = selectedField.i18List.filter((x) => selectedDefaultLanguage !== x.i18Code);
      const updatedField = {
        ...selectedField,
        parentId: values.groupId,
        i18List: [
          ...oldI18List,
          {
            i18Code: selectedDefaultLanguage,
            i18Name: values.fieldTitle,
          },
        ],
        members,
        updated: true,
      };
      dispatch(updateCustomFields(updatedField));
    }
    onCancel();
  };

  const onAddGenericField = (values) => {
    if (isNew) {
      const newField = {
        id: uuid(),
        parentId: values.groupId,
        i18List: [
          {
            i18Code: selectedDefaultLanguage,
            i18Name: values.fieldTitle,
          },
        ],
        type: fieldFormType,
        size: 0,
        default: false,
        members: [],
        isNew,
      };

      dispatch(addCustomField(newField));
    } else {
      const oldI18List = selectedField.i18List.filter((x) => selectedDefaultLanguage !== x.i18Code);
      const updatedField = {
        ...selectedField,
        parentId: values.groupId,
        i18List: [
          ...oldI18List,
          {
            i18Code: selectedDefaultLanguage,
            i18Name: values.fieldTitle,
          },
        ],
        updated: true,
      };
      dispatch(updateCustomFields(updatedField));
    }
    onCancel();
  };

  const Title = () => {
    const titleMap = new Map([
      [FieldType.GROUP, t('custom_field_group_fields')],
      [FieldType.TEXT, t('custom_field_text_fields')],
      [FieldType.TEXT_AREA, t('custom_field_textarea_field')],
      [FieldType.NUMBER, t('custom_field_number_fields')],
      [FieldType.CURRENCY, t('custom_field_currency_fields')],
      [FieldType.LIST, t('custom_field_simple_list_fields')],
      [FieldType.MULTI_LIST, t('custom_field_multiple_selection_list_fields')],
      [FieldType.OPTION, t('custom_field_option_fields')],
      [FieldType.CALENDAR, t('custom_field_date_fields')],
    ]);
    return (
      <Row className="row-title">
        <Col sm={12}>
          <h3>{titleMap.get(fieldFormType)}</h3>
        </Col>
      </Row>
    );
  };

  const FormBody = () => {
    const fieldForms = new Map([
      [
        FieldType.GROUP,
        <GroupForm defaultValue={selectedField} onCancel={onCancel} onSubmit={onAddGroupField} />,
      ],
      [
        FieldType.TEXT,
        <TextFieldForm
          defaultValue={selectedField}
          onCancel={onCancel}
          onSubmit={onAddTextField}
        />,
      ],
      [
        FieldType.TEXT_AREA,
        <TextAreaForm defaultValue={selectedField} onCancel={onCancel} onSubmit={onAddTextArea} />,
      ],
      [
        FieldType.NUMBER,
        <GenericForm
          defaultValue={selectedField}
          onCancel={onCancel}
          onSubmit={onAddGenericField}
        />,
      ],
      [
        FieldType.CURRENCY,
        <GenericForm
          defaultValue={selectedField}
          onCancel={onCancel}
          onSubmit={onAddGenericField}
        />,
      ],
      [
        FieldType.LIST,
        <ListForm defaultValue={selectedField} onCancel={onCancel} onSubmit={onAddListFields} />,
      ],
      [
        FieldType.MULTI_LIST,
        <ListForm
          defaultValue={selectedField}
          multiple
          onCancel={onCancel}
          onSubmit={onAddListFields}
        />,
      ],
      [
        FieldType.OPTION,
        <GenericForm
          defaultValue={selectedField}
          onCancel={onCancel}
          onSubmit={onAddGenericField}
        />,
      ],
      [
        FieldType.CALENDAR,
        <GenericForm
          defaultValue={selectedField}
          onCancel={onCancel}
          onSubmit={onAddGenericField}
        />,
      ],
    ]);
    return fieldFormType ? fieldForms.get(fieldFormType) ?? null : null;
  };

  return (
    <FieldFormWrapper>
      <Title />
      <FormBody />
    </FieldFormWrapper>
  );
};

FieldForm.defaultProps = {
  isNew: false,
  fieldFormType: '',
  selectedField: undefined,
};

FieldForm.propTypes = {
  isNew: PropTypes.bool,
  fieldFormType: PropTypes.string,
  selectedField: CustomFieldProps,
  onCancel: PropTypes.func.isRequired,
};

export default FieldForm;
